import assert from 'assert';
import produce from 'immer';

import {
  ProcessClientOrigin,
  ProcessFlowchartNodeDetailsType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  FlowchartBaseNode,
  FlowchartBaseEdge,
  Flowchart,
} from 'processes/graph-builder/flowchart-viz/FlowchartVizConstants';

import {ProcessFlowchartDataQuery} from '../__generated/ProcessFlowchartDataQuery';
import {ProcessTaskGroupFlowchartDataQuery} from '../__generated/ProcessTaskGroupFlowchartDataQuery';
import {ProcessInteractiveFlowchart, ProcessStaticFlowchart} from '../ProcessFlowchartVizConstants';

type ExecutionFlowchartData = NonNullable<
  ExtractByTypename<
    ProcessFlowchartDataQuery['processTasks'][number],
    'ProcessTaskNode'
  >['executionFlowchartData']
>;

type ProcessFlowchartRootTask = ProcessFlowchartDataQuery['processTasks'][number];
type ProcessFlowchartRootProcess = ProcessTaskGroupFlowchartDataQuery['process'];

export function getEdgeHash(fromNodeClientId: string, toNodeClientId: string): string {
  return `${fromNodeClientId}-${toNodeClientId}`;
}

interface TaskWithExecutionFlowchartData {
  __typename: 'ProcessTaskNode';
  executionFlowchartData: ExecutionFlowchartData | null;
}

export function getExecutionFlowchartData(
  task: TaskWithExecutionFlowchartData
): ExecutionFlowchartData | null {
  return task.executionFlowchartData;
}

function getEdgesUsedInFlowchartRootedAtTask(flowchartTask: ProcessFlowchartRootTask): Set<string> {
  if (isInaccessible(flowchartTask) || getExecutionFlowchartData(flowchartTask) == null) {
    return new Set();
  }

  const flowchartDescendentTasks = (getExecutionFlowchartData(flowchartTask)?.flowchartTasks ?? []).map(
    ({task, nodeConfig}) => ({...task, process: {...task.process, nodeConfig}})
  );
  const usedEdges: Set<string> = new Set();
  for (const taskOrOutput of [...flowchartTask.outputs, ...flowchartDescendentTasks]) {
    const predecessorNodes = taskOrOutput.flowchartPredecessorNodes;
    const toNodeOrRootId = taskOrOutput.process.nodeConfig?.id;
    assert(
      toNodeOrRootId != null,
      // once upon a time there were dummy output nodes. These should not exist anymore
      'Should not be mapping to dummy output node when V2 flowcharts are enabled'
    );

    for (const {id} of predecessorNodes) {
      usedEdges.add(getEdgeHash(id, toNodeOrRootId));
    }
  }

  return usedEdges;
}

export function getAllEdgesUsedInFlowchartTasksLineage(
  flowchartTasks: Array<ProcessFlowchartRootTask>
): Set<string> {
  return flowchartTasks.reduce(
    (acc, task) => new Set([...acc, ...getEdgesUsedInFlowchartRootedAtTask(task)]),
    new Set<string>()
  );
}

/**
 * Finds all paths between any two non-router nodes in the flowchart which is made up entirely of router nodes.
 *
 * In this graph:
 *
 * root --> A --> <R> --> B --> <R2> --> C
 *                  \             \
 *                    D             E
 *
 * This function would return the paths:
 * - [A, B]
 * - [A, D]
 * - [B, C]
 * - [B, E]
 *
 * @param flowchart The flowchart
 * @returns All paths between two non-router nodes where the path contains only router node, along with the edge IDs along that path
 */
function getPathsWithOnlyRouters<TNode extends FlowchartBaseNode, TEdge extends FlowchartBaseEdge>(
  flowchart: Flowchart<TNode, TEdge>
): Array<{fromNodeId: string; toNodeId: string; relevantEdgeIds: Array<string>}> {
  const pathsIncludingRouter: Array<{fromNodeId: string; toNodeId: string; relevantEdgeIds: Array<string>}> =
    [];
  const visitedNodeIds = new Set();
  const rootNode = flowchart.nodes.find(
    (node) => node.type === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS
  );
  assert(rootNode != null, 'Root node must exist');
  const stack: Array<{
    pathStartId: string;
    currentNodeId: string;
    relevantEdgeIds: Array<string>;
  }> = [
    {
      pathStartId: rootNode.clientId,
      currentNodeId: rootNode.clientId,
      relevantEdgeIds: [],
    },
  ];

  while (stack.length > 0) {
    const {currentNodeId, pathStartId, relevantEdgeIds} = stack.pop()!;

    const currentNode = flowchart.nodes.find(({clientId}: TNode) => clientId === currentNodeId);
    assert(currentNode != null, 'Node must exist');

    // Any path which includes a router node must have at least 2 edges - one from the first node to the router and
    // the second from the router to the first non-router node. So we only collect paths which more than one relevant edge.
    if (
      currentNode.type !== ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS &&
      relevantEdgeIds.length > 1
    ) {
      pathsIncludingRouter.push({fromNodeId: pathStartId, toNodeId: currentNodeId, relevantEdgeIds});
    }

    if (visitedNodeIds.has(currentNodeId)) {
      continue;
    }
    visitedNodeIds.add(currentNodeId);

    for (const edge of flowchart.edges) {
      if (edge.fromNodeClientId !== currentNodeId) {
        continue;
      }
      if (currentNode.type === ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS) {
        stack.push({
          pathStartId,
          currentNodeId: edge.toNodeClientId,
          relevantEdgeIds: [...relevantEdgeIds, edge.clientId],
        });
      } else {
        stack.push({
          pathStartId: currentNodeId,
          currentNodeId: edge.toNodeClientId,
          relevantEdgeIds: [edge.clientId],
        });
      }
    }
  }

  return pathsIncludingRouter;
}

export function mergeTaskGroupLineageIntoFlowchart<
  TFlowchart extends ProcessStaticFlowchart | ProcessInteractiveFlowchart,
>({
  flowchart,
  rootProcess,
  clientOrigin,
  processBeingViewed,
  shouldLinkProcess,
  onSetProcess,
}: {
  flowchart: TFlowchart;
  rootProcess: ProcessFlowchartRootProcess;
  clientOrigin?: ProcessClientOrigin;
  processBeingViewed?: string;
  shouldLinkProcess?: boolean;
  onSetProcess?: (processId: string) => void;
}): TFlowchart {
  if (isInaccessible(rootProcess) || rootProcess.childProcesses == null) {
    return flowchart;
  }

  const childProcesses = rootProcess.childProcesses;
  return produce(flowchart, (draftFlowchart) => {
    for (const node of draftFlowchart.nodes) {
      const process = [...childProcesses, rootProcess].find(
        (process) => process && !isInaccessible(process) && process.nodeConfig?.id === node.clientId
      );
      if (process == null || isInaccessible(process)) {
        continue;
      }

      const statusCounts = process.taskStatusCounts.map((statusCount) => ({
        statusType: statusCount.status.statusType,
        count: statusCount.count,
      }));
      node.statusCounts = statusCounts;
      node.isHighlighted = process.id === processBeingViewed;
      node.linkUrl = !node.isInaccessible && statusCounts.length > 0 ? process.flowchartNodeUrl : null;
      if (!node.isInaccessible && statusCounts.length > 0 && onSetProcess != null) {
        node.onClick = () => {
          onSetProcess(process.id);
        };
      }
      if (process.hasTasksWithFailedMappings) {
        if (node.errors == null) {
          node.errors = [];
        }
        node.errors.push('Fix mapping errors to continue this workflow.');
      }
    }

    const usedLineageEdges = new Set(
      [rootProcess, ...rootProcess.childProcesses].flatMap((process) => {
        if (isInaccessible(process)) {
          return [];
        }
        return process.flowchartPredecessorNodes.map((predecessorNode) =>
          getEdgeHash(predecessorNode.id, process.nodeConfig?.id || '')
        );
      })
    );
    for (const edge of draftFlowchart.edges) {
      edge.hasBeenUsedToCreateData = usedLineageEdges.has(
        getEdgeHash(edge.fromNodeClientId, edge.toNodeClientId)
      );
    }

    const pathsWithOnlyRouters = getPathsWithOnlyRouters(flowchart);
    for (const {fromNodeId, toNodeId, relevantEdgeIds} of pathsWithOnlyRouters) {
      if (usedLineageEdges.has(getEdgeHash(fromNodeId, toNodeId))) {
        for (const edgeId of relevantEdgeIds) {
          const edge = draftFlowchart.edges.find((e) => e.clientId === edgeId);
          if (edge != null) {
            edge.hasBeenUsedToCreateData = true;
          }
        }
      }
    }
  });
}
